import React, { useEffect } from 'react';
import styled, { css } from 'styled-components/macro';
import { variant, compose } from 'styled-system';

import {
  safeClick,
  keyboardClick,
  trackingId,
  useWithRtl,
  rem,
} from '@blueprint/ui';

import { Link } from '../Typography';
import Icon from '../Icon';

/**
 * create custom Icon
 */
const ExpandLinkLinkIcon = styled(Icon)(
  compose(
    variant({
      prop: 'mode',
      scale: 'links.expandLink.icon.variantMapping',
    }),
    variant({
      prop: 'direction',
      scale: 'links.expandLink.icon.directionMapping',
    }),
    variant({
      prop: 'bodyDirection',
      scale: 'links.expandLink.icon.bodyDirectionMapping',
    }),
    variant({
      prop: 'iconSize',
      scale: 'links.expandLink.icon.sizeMapping',
    }),
  ),
  css`
    margin-top: ${rem(0)};
    width: ${rem(20)};
    height: ${rem(20)};
  `,
);

const ExpandLinkWrapper = styled(Link)`
  display: flex;
  ${({ multiLine }) =>
    multiLine
      ? css`
          align-items: stretch;

          > span:last-child {
            transform: translateY(2px);
          }
        `
      : css`
          align-items: center;
        `}
`;

/**
 * ChevronLink
 */
const ExpandLink = ({
  variant = 'body1',
  direction = 'right',
  mode = 'light',
  active: propsActive = false,
  controlled = false,
  ariaControlId,
  onClick,
  multiLine = false,
  children,
  ...rest
}) => {
  const [active, setActive] = React.useState(propsActive);
  const iconProps = (name) => ({
    name,
    mode,
    component: 'span',
    alignItems: 'center',
    size: '100%',
    direction,
    bodyDirection: variant.indexOf('body') !== -1 ? direction : null,
    key: name,
  });

  useEffect(() => {
    setActive(propsActive);
  }, [propsActive]);

  // assign the direction
  const startWithIconOnRight = direction === 'right';
  const startWithIconOnLeft = direction === 'left';

  const icon = (
    <ExpandLinkLinkIcon
      iconSize={variant}
      svgProps={{ color: 'inherit' }}
      {...iconProps(active ? 'ChevronUpThick' : 'ChevronDownThick')}
    />
  );

  // handle click
  const handleActiveState = (event) => {
    safeClick(onClick, event);

    if (!controlled) {
      setActive((prevState) => !prevState);
    }
  };

  // create elements
  const iconElement = useWithRtl(icon, icon);

  return (
    // @ts-ignore
    <ExpandLinkWrapper
      tabIndex={0}
      aria-expanded={active}
      aria-controls={ariaControlId}
      data-testid="ExpandLink"
      data-trackid={trackingId('ExpandLink')}
      onClick={handleActiveState}
      onKeyDown={keyboardClick(handleActiveState)}
      variant={variant}
      textAlign={direction === 'left' ? 'right' : 'left'}
      mode={mode}
      multiLine={multiLine}
      {...rest}
    >
      {startWithIconOnLeft && iconElement}
      <span>{children}</span>
      {startWithIconOnRight && iconElement}
    </ExpandLinkWrapper>
  );
};

ExpandLink.displayName = 'ExpandLink';

export default ExpandLink;
